import { z } from "zod";

import { SaleTypeForTerm } from "@/lib/gql/__generated__/graphql";
import {
	BasicCustomMailOptionalItemOverrideInputSchema,
	BasicCustomMailRequiredItemOverrideInputSchema,
	EmailTemplateReservationInputSchema,
	LongStayParamsInputSchema,
	PlanInputSchema,
	PriceRankRoomItemInputSchema,
	QuestionItemInputSchema,
	RoomPriceObjectInputSchema,
	ShortStayParamsInputSchema,
	StaticRoomPriceParamsInputSchema,
} from "@/lib/zod/__generated__/validation-schema";

import {
	chillnnDateZodSchema,
	dayOfTheWeekZodSchema,
	includedMealsZodSchema,
	kidsPolicyZodSchema,
	priceObjectZodSchema,
	requiredPrepMilliSecondBeforeDateZodSchema,
	requiredPrepMilliSecondBeforeHourZodSchema,
	s3ObjectZodSchema,
} from "@/components/features/_shared/validation-schema/validation-schema";

export const dayTripPlanInputZodSchema = PlanInputSchema.extend({
	name: z.string().min(1).max(30),
	mainTarget: z.string().max(30).nullish(),
	mainKeyVisual: s3ObjectZodSchema,
	keyVisuals: z.array(s3ObjectZodSchema),

	availableRoomIDs: z.array(z.string()),
	isShowRoomForGuest: z.boolean(),

	price: priceObjectZodSchema,
	kidsPolicy: kidsPolicyZodSchema.nullish(),
	coSleepingKidsPrice: priceObjectZodSchema.nullish(),

	maxPeopleNumPerRoom: z.number().min(0).max(100).nullish(),
	minimumPeopleNumPerRoom: z.number().min(1).max(100),

	minRoomNum: z.number().min(1).max(30),
	maxRoomNum: z.number().min(1).max(100).nullish(),

	requiredPrepMillisecondBeforeDate: requiredPrepMilliSecondBeforeDateZodSchema,
	requiredPrepMillisecondBeforeHour: requiredPrepMilliSecondBeforeHourZodSchema,

	saleStart: chillnnDateZodSchema.nullish().or(z.literal("")),
	saleEnd: chillnnDateZodSchema.nullish().or(z.literal("")),

	stayableStart: chillnnDateZodSchema.nullish(),
	stayableEnd: chillnnDateZodSchema.nullable().or(z.literal("")),

	saleStartBeforeDate: z.number().int().min(1).max(1_000, { message: "1000日以内に設定してください" }).nullish(),

	maxDailyStockNum: z.number().int().min(1).max(100_000).nullish(),

	maxStockNum: z.number().int().min(1).max(100_000).nullish(),

	checkInAvailableDayOfTheWeek: dayOfTheWeekZodSchema,
	stayableDayOfTheWeek: dayOfTheWeekZodSchema,
	isHolidayStayable: z.boolean().nullish(),
	isHolidayCheckInAvailable: z.boolean().nullish(),

	includedMeals: includedMealsZodSchema.nullish(),

	longStayParams: LongStayParamsInputSchema.extend({
		minimumStayDates: z.union([
			z
				.number()
				.int()
				.min(0, { message: "日帰りプランの場合は0を設定する必要があります" })
				.max(0, { message: "日帰りプランの場合は0を設定する必要があります" }),
			z.undefined(),
		]),
	}).nullish(),

	shortStayParams: ShortStayParamsInputSchema.extend({
		maximumStayDates: z.union([
			z
				.number()
				.int()
				.min(1, { message: "日帰りプランの場合は1を設定する必要があります" })
				.max(1, { message: "日帰りプランの場合は1を設定する必要があります" }),
			z.undefined(),
		]),
	}).nullish(),

	questions: z.array(
		QuestionItemInputSchema.extend({
			question: z.string().min(1).max(8_000),
			options: z.array(z.string().min(1).max(500)).nullish(),
			required: z.boolean().nullish(),
		}),
	),

	emailTemplateReservation: EmailTemplateReservationInputSchema.extend({
		atCreated: z.string().max(3_000).nullish(),
		atUpdated: z.string().max(3_000).nullish(),
		atCanceled: z.string().max(3_000).nullish(),
		atCin: z.string().max(3_000).nullish(),
	}).nullish(),

	staticPriceParams: StaticRoomPriceParamsInputSchema.extend({
		staticRoomPrices: z.array(
			z.lazy(() =>
				PriceRankRoomItemInputSchema.extend({
					prices: z
						.array(
							z.lazy(() =>
								RoomPriceObjectInputSchema.extend({
									adultNum: z.number(),
									price: priceObjectZodSchema.nullish(),
								}),
							),
						)
						.nullish(),
					roomID: z.string(),
					roomName: z.string(),
				}),
			),
		),
	}).nullish(),

	// カスタムメール設定
	atCreated: BasicCustomMailRequiredItemOverrideInputSchema.extend({
		text: z.string().max(3_000),
		override: z.boolean(),
	}),
	atUpdated: BasicCustomMailRequiredItemOverrideInputSchema.extend({
		text: z.string().max(3_000),
		override: z.boolean(),
	}),
	atCanceled: BasicCustomMailRequiredItemOverrideInputSchema.extend({
		text: z.string().max(3_000),
		override: z.boolean(),
	}),
	atCheckInBefore_07days: BasicCustomMailOptionalItemOverrideInputSchema.extend({
		text: z.string().max(3_000),
		shouldSendEmail: z.boolean(),
		override: z.boolean(),
	}),
	atCheckInBefore_01days: BasicCustomMailOptionalItemOverrideInputSchema.extend({
		text: z.string().max(3_000),
		shouldSendEmail: z.boolean(),
		override: z.boolean(),
	}),
	atCheckInBefore_00days: BasicCustomMailOptionalItemOverrideInputSchema.extend({
		text: z.string().max(3_000),
		shouldSendEmail: z.boolean(),
		override: z.boolean(),
	}),
	atCheckOutAfter_01days: BasicCustomMailOptionalItemOverrideInputSchema.extend({
		text: z.string().max(3_000),
		shouldSendEmail: z.boolean(),
		override: z.boolean(),
	}),
}) // ↓ 複数の値によるバリデーションを設定するために、refineを使用している
	// 選択可能な部屋（部屋の項目を表示する場合は「1つ以上」を要求）
	.refine(
		({ isShowRoomForGuest, availableRoomIDs }) => {
			if (!isShowRoomForGuest) {
				return true;
			}
			return availableRoomIDs.length > 0;
		},
		{
			message: "予約可能な部屋は1つ以上選択してください",
			path: ["availableRoomIDs", "value"],
		},
	)
	// 選択可能な部屋（在庫として扱う場合は「ちょうど1つ」を要求）
	.refine(
		({ isShowRoomForGuest, availableRoomIDs }) => {
			if (isShowRoomForGuest) {
				return true;
			}

			return availableRoomIDs.length === 1;
		},
		{
			message: "予約可能な部屋は1つ選択してください",
			path: ["availableRoomIDs", "value"],
		},
	)
	// 添い寝の子供の料金設定
	.refine(
		({ coSleepingKidsPrice, isCoSleepingKidsPriceActive }) => {
			if (!isCoSleepingKidsPriceActive) {
				return true;
			}

			return typeof coSleepingKidsPrice?.amount === "number";
		},
		{
			message: "添い寝の子供の料金を設定してください",
			path: ["coSleepingKidsPrice", "amount"],
		},
	)
	// １度の予約につき宿泊可能な部屋
	.refine(
		({ maxRoomNum, minRoomNum }) => {
			if (typeof maxRoomNum === "number" && maxRoomNum < minRoomNum) {
				return false;
			}

			return true;
		},
		{
			message: "最高部屋数は最低部屋数以上になるように設定してください",
			path: ["maxRoomNum"],
		},
	)
	// 公開する期間
	.refine(
		({ saleStart, saleEnd, saleStartTime, saleEndTime, saleTypeForTerm }) => {
			if (saleTypeForTerm === SaleTypeForTerm.Limited) {
				if (!!saleStart && !!saleEnd) {
					// 同じ日付の場合は時刻もチェック
					if (saleStart === saleEnd && !!saleStartTime && !!saleEndTime) {
						return saleStartTime < saleEndTime;
					}
					// 異なる日付の場合は日付のみをチェック
					return saleStart <= saleEnd;
				}

				return true;
			}

			return true;
		},
		{
			message: "終了日時が開始日時以降になるように設定してください",
			path: ["saleEnd"],
		},
	)
	// 滞在可能な期間
	.refine(
		({ stayableStart, stayableEnd, saleTypeForTerm }) => {
			if (saleTypeForTerm === SaleTypeForTerm.Limited) {
				if (!!stayableStart && !!stayableEnd) {
					return stayableStart <= stayableEnd;
				}

				return !!stayableStart;
			}

			return true;
		},
		{
			message: "滞在可能な期間を設定してください",
			path: ["stayableStart"],
		},
	)
	// 最長連泊期間
	.refine(
		({ shortStayParams, longStayParams }) => {
			if (!longStayParams) {
				return true;
			}

			if (!shortStayParams) {
				return true;
			}

			// 最長連泊期間は最短連泊期間と同じか、それよりも短くなる必要がある
			return (longStayParams.minimumStayDates ?? 0) <= (shortStayParams.maximumStayDates ?? 1);
		},
		{
			message: "最長連泊期間は最短連泊期間よりも短くなるように設定してください",
			path: ["shortStayParams", "maximumStayDates"],
		},
	);

export type DayTripPlanInputZodSchemaType = z.infer<typeof dayTripPlanInputZodSchema>;
